import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";

import LabInvestigationService from "../labInvestigations/LabInvestigationService";
import { readingTag } from "../labInvestigations/data";
import { formatShortDate } from "../chiefComplaints/dateHelpers";
import ModalPortal from "../common/ModalPortal";

/**
 * Rich "All lab values" picker used when the user clicks the `+N more` /
 * `more` pill on the Lab strip inside the Add / Update Diagnosis dialogs.
 *
 * Mirrors the Lab Investigations section layout (Figma 3041:11675) so
 * clinicians see the exact same table shape they use daily:
 *   • flat table with a checkbox column instead of the section's
 *     "View trend" analysis column
 *   • loose (no-panel) tests at the top
 *   • panel groups with a header row (name · N entries · X flagged pill)
 *   • latest-per-test collapse (older entries feed the trend view, not this
 *     picker)
 *   • search box that narrows by name AND by formatted date
 *   • footer: "N selected — will be added to diagnosis evidence" · Cancel ·
 *     Add to evidence
 *
 * The helpers below (groupByPanel / groupByTest / formatRange) intentionally
 * mirror LabInvestigations.js — small, self-contained, and duplicating them
 * lets this picker evolve independently.
 */

const groupByPanel = (list) => {
  const loose = [];
  const buckets = new Map();
  for (const inv of list || []) {
    const key = inv.test && (inv.test.ltCode || inv.test.panel);
    if (!key) {
      loose.push(inv);
      continue;
    }
    if (!buckets.has(key)) {
      buckets.set(key, {
        key,
        panel: (inv.test && inv.test.panel) || "",
        entries: [],
        flaggedCount: 0,
        latestAt: 0,
      });
    }
    const b = buckets.get(key);
    b.entries.push(inv);
    if (inv.flag === "low" || inv.flag === "high") b.flaggedCount++;
    const t = new Date(inv.testDate).getTime();
    if (!isNaN(t) && t > b.latestAt) b.latestAt = t;
  }
  const groups = Array.from(buckets.values());
  groups.sort((a, b) => b.latestAt - a.latestAt);
  loose.sort((a, b) => new Date(b.testDate).getTime() - new Date(a.testDate).getTime());
  return { loose, groups };
};

const groupByTest = (entries) => {
  const buckets = new Map();
  for (const inv of entries) {
    const key = (inv.test && inv.test.sltCode) || (inv.test && inv.test.name) || "";
    if (!key) continue;
    if (!buckets.has(key)) buckets.set(key, []);
    buckets.get(key).push(inv);
  }
  const testGroups = Array.from(buckets.entries()).map(([key, list]) => {
    list.sort((a, b) => new Date(b.testDate).getTime() - new Date(a.testDate).getTime());
    return { key, latest: list[0] };
  });
  testGroups.sort(
    (a, b) => new Date(b.latest.testDate).getTime() - new Date(a.latest.testDate).getTime()
  );
  return testGroups;
};

const formatRange = (r) => {
  if (!r) return "—";
  if (r.low != null && r.high != null) return `${r.low}–${r.high}`;
  if (r.low != null) return `≥ ${r.low}`;
  if (r.high != null) return `≤ ${r.high}`;
  return r.text || "—";
};

// Search matches name, panel, the latest reading's tag, OR the formatted date
// so users can type "13 May" and filter to that day.
const matchesQuery = (inv, needle) => {
  if (!needle) return true;
  const n = needle.trim().toLowerCase();
  if (!n) return true;
  const name = (inv.test && inv.test.name && inv.test.name.toLowerCase()) || "";
  const date = formatShortDate(inv.testDate).toLowerCase();
  const panel = (inv.test && inv.test.panel && inv.test.panel.toLowerCase()) || "";
  const tag = readingTag(inv.test).toLowerCase();
  return name.includes(n) || date.includes(n) || panel.includes(n) || (!!tag && tag.includes(n));
};

const Row = ({ inv, selected, onToggle }) => {
  const flagged = inv.flag === "low" || inv.flag === "high";
  const id = String(inv.investigationId);
  const tag = readingTag(inv.test);
  return (
    <div
      className={`dxlp-row li3-row${flagged ? " is-flagged" : ""}${selected ? " is-selected" : ""}`}
      role="row"
      onClick={() => onToggle(id)}
    >
      <span className="dxlp-c-check" role="cell">
        <span className={`dxlp-check${selected ? " is-on" : ""}`} aria-hidden="true">
          {selected && (
            <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2.4">
              <path d="M4 10.5l4 4 8-9" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          )}
        </span>
      </span>
      <span className="li3-cell li3-c-name" role="cell">
        {inv.test && inv.test.name}
        {tag ? <span className="li-tag">{tag}</span> : null}
      </span>
      <span className="li3-cell li3-c-date" role="cell">{formatShortDate(inv.testDate)}</span>
      <span className="li3-cell li3-c-result" role="cell">
        {inv.flag === "high" && <span className="li3-arr" aria-label="High">↑</span>}
        {inv.flag === "low" && <span className="li3-arr" aria-label="Low">↓</span>}
        {inv.value}
      </span>
      <span className="li3-cell li3-c-units" role="cell">{(inv.test && inv.test.unit) || "—"}</span>
      <span className="li3-cell li3-c-range" role="cell">{formatRange(inv.test && inv.test.refRange)}</span>
    </div>
  );
};
Row.propTypes = {
  inv: PropTypes.object.isRequired,
  selected: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
};

const LabPickerModal = ({ patientId, initialSelectedIds, onCommit, onClose }) => {
  const [investigations, setInvestigations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(() => new Set((initialSelectedIds || []).map(String)));
  const [query, setQuery] = useState("");

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    if (!patientId) return;
    setLoading(true);
    const svc = new LabInvestigationService();
    svc.list(
      patientId,
      (res) => {
        setInvestigations((res && res.investigations) || []);
        setLoading(false);
      },
      () => {
        setInvestigations([]);
        setLoading(false);
      }
    );
  }, [patientId]);

  const { loose, groups, totalTests } = useMemo(() => {
    const filtered = investigations.filter((inv) => matchesQuery(inv, query));
    const { loose: l, groups: g } = groupByPanel(filtered);
    let total = 0;
    for (const g1 of g) {
      const collapsed = groupByTest(g1.entries);
      g1.testGroups = collapsed;
      total += collapsed.length;
    }
    // Loose measurements are also latest-per-test to keep the picker tidy.
    const looseCollapsed = groupByTest(l);
    total += looseCollapsed.length;
    return { loose: looseCollapsed, groups: g, totalTests: total };
  }, [investigations, query]);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const commit = () => {
    onCommit(Array.from(selected));
    onClose();
  };

  const selectedCount = selected.size;

  return (
    <ModalPortal>
      <div className="cc-modal-backdrop" onMouseDown={onClose}>
        <div
          className="cc-modal dxlp-modal"
          onMouseDown={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
        >
          <header className="cc-modal-head">
            <div>
              <span className="cc-modal-title">All lab values</span>
              <span className="cc-modal-sub"> · {totalTests} parameters</span>
            </div>
            <button type="button" className="cc-modal-close" onClick={onClose} aria-label="Close">
              ×
            </button>
          </header>
  
          <div className="cc-modal-body dxlp-body">
            <div className="sec-search dxlp-search">
              <svg
                className="sec-search-icon"
                viewBox="0 0 20 20"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                aria-hidden="true"
              >
                <circle cx="9" cy="9" r="6" />
                <path d="M14 14l4 4" strokeLinecap="round" />
              </svg>
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by name or date…"
              />
            </div>
  
            <div className="dxlp-table" role="table">
              <div className="dxlp-thead" role="row">
                <span className="dxlp-c-check" role="columnheader" aria-label="Select" />
                <span role="columnheader">Investigation</span>
                <span role="columnheader">Latest test date</span>
                <span role="columnheader">Result</span>
                <span role="columnheader">Units</span>
                <span role="columnheader">Ref. range</span>
              </div>
  
              {loading && <div className="dxlp-status">Loading lab values…</div>}
              {!loading && loose.length === 0 && groups.length === 0 && (
                <div className="dxlp-status">No matches.</div>
              )}
  
              {/* Loose (no-panel) tests up top. */}
              {loose.map((tg) => (
                <Row
                  key={tg.key}
                  inv={tg.latest}
                  selected={selected.has(String(tg.latest.investigationId))}
                  onToggle={toggle}
                />
              ))}
  
              {/* Panel groups. */}
              {groups.map((g) => (
                <React.Fragment key={g.key}>
                  <div className="dxlp-panel-band" role="row">
                    <span className="dxlp-panel-name">{g.panel || "Panel"}</span>
                    <span className="dxlp-panel-pill">{g.testGroups.length} entries</span>
                    {g.flaggedCount > 0 && (
                      <span className="dxlp-panel-pill dxlp-panel-flag">
                        <span className="dxlp-pill-dot" aria-hidden="true" />
                        {g.flaggedCount} flagged
                      </span>
                    )}
                  </div>
                  {g.testGroups.map((tg) => (
                    <Row
                      key={tg.key}
                      inv={tg.latest}
                      selected={selected.has(String(tg.latest.investigationId))}
                      onToggle={toggle}
                    />
                  ))}
                </React.Fragment>
              ))}
            </div>
          </div>
  
          <footer className="cc-modal-foot dxlp-foot">
            <span className="dxlp-count">
              <strong>{selectedCount} selected</strong> — will be added to diagnosis evidence
            </span>
            <button type="button" className="sec-btn sec-btn-ghost" onClick={onClose}>
              Cancel
            </button>
            <button type="button" className="sec-btn sec-btn-primary" onClick={commit}>
              Add to evidence
            </button>
          </footer>
        </div>
      </div>
    </ModalPortal>
  );
};

LabPickerModal.propTypes = {
  patientId: PropTypes.number.isRequired,
  initialSelectedIds: PropTypes.array,
  onCommit: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default LabPickerModal;
